import { useMemo, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize2, Navigation, X } from "lucide-react";

const MAP_ZONES = [
    { id: "registration", name: "Registration", cat: "Arrival", x: 60, y: 560, w: 190, h: 80, desc: "Entry, check-in and digital badge collection — your gateway to the bazaar." },
    { id: "main-stage", name: "Main Stage", cat: "Stages", x: 450, y: 40, w: 300, h: 130, desc: "Grand opening, PitchYatra finale, the 2047 Showcase and the Awards Night." },
    { id: "mahatalks", name: "MahaTalks", cat: "Stages", x: 800, y: 60, w: 240, h: 110, desc: "The Voices of Bharat — keynotes, dialogues and debates." },
    { id: "bazaar", name: "Innovation Bazaar", cat: "Exhibition", x: 100, y: 190, w: 310, h: 200, desc: "10+ thematic pavilions and 120+ startups — where innovation comes alive." },
    { id: "pods", name: "Startup Pods", cat: "Exhibition", x: 460, y: 210, w: 230, h: 150, desc: "Compact plug-and-play pods with QR investor connect and live demos." },
    { id: "pitchyatra", name: "PitchYatra Arena", cat: "Stages", x: 740, y: 210, w: 250, h: 150, desc: "India's Ultimate Startup Showdown — five rounds, one stage." },
    { id: "adda", name: "Investor Adda", cat: "Connect", x: 100, y: 430, w: 230, h: 110, desc: "Speed meets, roundtables and family office dialogues." },
    { id: "impact", name: "Impact Kshetra", cat: "Exhibition", x: 370, y: 430, w: 200, h: 110, desc: "The Future Pavilion — social and environmental innovation." },
    { id: "labs", name: "Knowledge Labs", cat: "Learn", x: 610, y: 420, w: 200, h: 120, desc: "20+ masterclasses and innovation studios." },
    { id: "zones", name: "Special Zones", cat: "Exhibition", x: 850, y: 420, w: 210, h: 120, desc: "Global Startup Street, Women in Innovation Arena, Creator Economy and more." },
    { id: "food", name: "Food & Experience", cat: "Experience", x: 440, y: 570, w: 280, h: 85, desc: "Taste of Bharat, Bazaar Beats and the evening experiences." },
];

const CATS = ["All", "Stages", "Exhibition", "Connect", "Learn", "Experience", "Arrival"];
const CAT_COLORS = {
    Stages: "#4A0E18",
    Exhibition: "#2E0C14",
    Connect: "#2E2410",
    Learn: "#260A11",
    Experience: "#241019",
    Arrival: "#22070E",
};

export default function EventMap() {
    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [cat, setCat] = useState("All");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [directions, setDirections] = useState(false);
    const drag = useRef(null);
    const moved = useRef(false);
    const wrapRef = useRef(null);

    const visible = useMemo(
        () =>
            MAP_ZONES.filter(
                (z) =>
                    (cat === "All" || z.cat === cat) &&
                    (!q.trim() || z.name.toLowerCase().includes(q.trim().toLowerCase())),
            ),
        [cat, q],
    );

    const onDown = (e) => {
        drag.current = { sx: e.clientX, sy: e.clientY, ox: pan.x, oy: pan.y };
        moved.current = false;
    };
    const onMove = (e) => {
        if (!drag.current) return;
        const dx = e.clientX - drag.current.sx;
        const dy = e.clientY - drag.current.sy;
        if (Math.abs(dx) + Math.abs(dy) > 5) moved.current = true;
        setPan({ x: drag.current.ox + dx, y: drag.current.oy + dy });
    };
    const onUp = () => {
        drag.current = null;
        setTimeout(() => (moved.current = false), 0);
    };

    const reg = MAP_ZONES[0];
    const regCenter = { x: reg.x + reg.w / 2, y: reg.y + reg.h / 2 };

    return (
        <div className="card-luxe overflow-hidden" data-testid="event-map">
            <div className="flex flex-wrap items-center gap-3 p-4 border-b hairline">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search zones…"
                    data-testid="map-search-input"
                    className="field !w-48 !py-2 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                    {CATS.map((c) => (
                        <button key={c} onClick={() => setCat(c)} data-testid={`map-filter-${c.toLowerCase()}`}
                            className={`chip ${cat === c ? "chip-on" : ""}`}>
                            {c}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex gap-2">
                    <button onClick={() => setScale((s) => Math.min(2.4, s + 0.25))} aria-label="Zoom in" data-testid="map-zoom-in"
                        className="w-9 h-9 border hairline flex items-center justify-center text-[var(--champagne)] hover:border-[var(--line)] transition-colors">
                        <ZoomIn size={15} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => setScale((s) => Math.max(0.7, s - 0.25))} aria-label="Zoom out" data-testid="map-zoom-out"
                        className="w-9 h-9 border hairline flex items-center justify-center text-[var(--champagne)] hover:border-[var(--line)] transition-colors">
                        <ZoomOut size={15} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => { setScale(1); setPan({ x: 0, y: 0 }); setDirections(false); }} aria-label="Reset map" data-testid="map-reset"
                        className="w-9 h-9 border hairline flex items-center justify-center text-[var(--champagne)] hover:border-[var(--line)] transition-colors">
                        <Maximize2 size={14} strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            <div
                ref={wrapRef}
                className="relative h-[420px] md:h-[520px] overflow-hidden cursor-grab active:cursor-grabbing touch-none bg-[#160409]"
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerLeave={onUp}
            >
                <div className="absolute inset-0 jaali opacity-30" />
                <svg
                    viewBox="0 0 1140 700"
                    className="absolute inset-0 w-full h-full"
                    style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`, transition: drag.current ? "none" : "transform .45s cubic-bezier(.22,1,.36,1)" }}
                >
                    <rect x="30" y="20" width="1080" height="660" fill="none" stroke="rgba(201,162,75,0.25)" strokeWidth="1.5" />
                    <rect x="42" y="32" width="1056" height="636" fill="none" stroke="rgba(201,162,75,0.12)" strokeWidth="1" />
                    {directions && selected && (
                        <line
                            x1={regCenter.x} y1={regCenter.y}
                            x2={selected.x + selected.w / 2} y2={selected.y + selected.h / 2}
                            stroke="var(--champagne)" strokeWidth="2" className="dash-flow"
                        />
                    )}
                    {MAP_ZONES.map((z) => {
                        const dim = !visible.includes(z);
                        const isSel = selected?.id === z.id;
                        return (
                            <g key={z.id}
                                onClick={() => { if (moved.current) return; setSelected(z); setDirections(false); }}
                                className="cursor-pointer"
                                opacity={dim ? 0.16 : 1}
                                style={{ transition: "opacity .4s" }}
                                data-testid={`map-zone-${z.id}`}
                            >
                                <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="2"
                                    fill={CAT_COLORS[z.cat]}
                                    stroke={isSel ? "var(--champagne)" : "rgba(201,162,75,0.4)"}
                                    strokeWidth={isSel ? 2 : 1}
                                />
                                {isSel && (
                                    <rect x={z.x - 5} y={z.y - 5} width={z.w + 10} height={z.h + 10} rx="3"
                                        fill="none" stroke="rgba(232,207,143,0.4)" strokeWidth="1" className="pulse-soft" />
                                )}
                                <text x={z.x + z.w / 2} y={z.y + z.h / 2} textAnchor="middle" dominantBaseline="middle"
                                    fill="var(--ivory)" fontSize="13" letterSpacing="2.5"
                                    style={{ textTransform: "uppercase", fontFamily: "Jost, sans-serif", pointerEvents: "none" }}>
                                    {z.name}
                                </text>
                                <text x={z.x + z.w / 2} y={z.y + z.h / 2 + 18} textAnchor="middle"
                                    fill="rgba(201,162,75,0.8)" fontSize="9" letterSpacing="3"
                                    style={{ textTransform: "uppercase", fontFamily: "Jost, sans-serif", pointerEvents: "none" }}>
                                    {z.cat}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>

            {selected && (
                <div className="border-t hairline p-5 flex flex-wrap items-center gap-5" data-testid="map-zone-panel">
                    <div className="min-w-0 flex-1">
                        <p className="text-[0.58rem] tracking-[0.34em] uppercase text-[var(--gold)]">{selected.cat}</p>
                        <h4 className="font-display text-xl text-[var(--ivory)] mt-1">{selected.name}</h4>
                        <p className="text-sm text-muted-luxe mt-1.5">{selected.desc}</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setDirections(!directions)} data-testid="map-directions-btn" className="btn btn-outline !py-2.5 !px-5">
                            <Navigation size={12} /> {directions ? "Hide Route" : "Directions"}
                        </button>
                        <button onClick={() => { setSelected(null); setDirections(false); }} aria-label="Close zone panel" data-testid="map-panel-close"
                            className="w-10 h-10 border hairline flex items-center justify-center text-[var(--muted)] hover:text-[var(--champagne)] transition-colors">
                            <X size={15} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
